import { useEffect, useState } from "react";
import { Download, FileText, BookOpen } from "lucide-react";
import api from "../lib/api";
import SafeImage from "../components/site/SafeImage";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";

const Guides = () => {
  usePageMeta({
    title: "Free Patient Guides & Downloads | Dr. Shantanu Kumar Gupta",
    path: "/guides",
    description: "Downloadable PDF guides on cataract surgery, diabetic eye screening and dry-eye care — reviewed by Dr. Shantanu Kumar Gupta.",
  });
  usePageJsonLd(graph([breadcrumb([{ name: "Home", path: "/" }, { name: "Guides", path: "/guides" }])]));
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/guides").then((r) => setItems(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="guides-page">
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2"><BookOpen size={12} /> Free downloads</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Patient guides — free to download, always up to date.
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">
          Printable PDFs on cataract surgery, diabetic eye screening, dry eye and more — written and reviewed by Dr. Shantanu Kumar Gupta.
          Save, print, share with family.
        </p>
      </section>

      <section className="py-10 container-page">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guides-grid">
          {items.map((g) => {
            const canDownload = !!g.file_url;
            return (
              <article
                key={g.slug}
                className="rounded-2xl border border-border overflow-hidden bg-card flex flex-col"
                data-testid={`guide-${g.slug}`}
              >
                {g.cover_image && (
                  <SafeImage
                    src={g.cover_image}
                    alt={g.title}
                    label={g.title}
                    className="w-full aspect-[16/10] border-b border-border"
                  />
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mb-2">{g.category}</div>
                  <h2 className="font-serif text-xl leading-snug">{g.title}</h2>
                  <p className="mt-3 text-sm text-[hsl(var(--foreground))]/75 leading-relaxed flex-1">{g.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                      {g.pages ? `${g.pages} pages` : ""} {g.pages && g.language ? "·" : ""} {g.language === "hi" ? "हिन्दी" : "English"}
                    </div>
                    {canDownload ? (
                      <a
                        href={g.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary !py-2 !px-4 text-xs"
                        data-testid={`guide-download-${g.slug}`}
                        onClick={() => window.plausible && window.plausible("Guide Download", { props: { slug: g.slug } })}
                      >
                        <Download size={13} /> Download
                      </a>
                    ) : (
                      <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1">
                        <FileText size={12} /> Coming soon
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
          {items.length === 0 && (
            <div className="col-span-full py-20 text-center text-muted-foreground border border-dashed border-border rounded-xl">
              New patient guides will be published here shortly.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Guides;
